import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Users, Save, Loader2, Ban, Check, RotateCcw } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const OverrideQuotasTab = ({ isArabic }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [edits, setEdits] = useState({}); // user_id -> {limit, disabled}

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/loyalty/override-quotas');
      setItems(res.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isArabic ? 'فشل التحميل' : 'Failed'));
    } finally {
      setLoading(false);
    }
  }, [isArabic]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async (uid, payload) => {
    setSaving(uid);
    try {
      const res = await api.put(`/loyalty/override-quotas/${uid}`, payload);
      const q = res.data?.quota;
      setItems((curr) =>
        curr.map((it) =>
          it.user_id === uid
            ? {
                ...it,
                limit: q?.limit ?? it.limit,
                disabled: q?.disabled ?? it.disabled,
                used: q?.used ?? it.used,
                remaining:
                  (q?.limit ?? it.limit) > 0
                    ? Math.max(0, (q?.limit ?? it.limit) - (q?.used ?? it.used))
                    : null,
              }
            : it
        )
      );
      setEdits((curr) => ({ ...curr, [uid]: undefined }));
      toast.success(isArabic ? 'تم الحفظ' : 'Saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || (isArabic ? 'فشل الحفظ' : 'Failed'));
    } finally {
      setSaving(null);
    }
  };

  const toggleDisabled = (uid, currentDisabled) => {
    handleSave(uid, { disabled: !currentDisabled });
  };

  const saveLimit = (uid) => {
    const edit = edits[uid];
    if (!edit) return;
    handleSave(uid, { limit: Number(edit.limit) });
  };

  const resetUsed = (uid, name) => {
    if (!window.confirm(isArabic ? `إعادة عدّاد ${name} إلى الصفر؟` : `Reset ${name}'s counter?`)) return;
    handleSave(uid, { reset_used: true });
  };

  const totalUsed = items.reduce((s, it) => s + (it.used || 0), 0);

  return (
    <Card className="bg-card border-white/10 overflow-hidden" data-testid="override-quotas-panel">
      <div className="p-4 border-b border-white/10">
        <h3 className="font-bold flex items-center gap-2">
          <Users className="w-4 h-4 text-amber-400" />
          {isArabic ? 'حصص استخدام رمز الإذن — لكل حلاق' : 'Override-Code Quotas — Per Barber'}
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          {isArabic
            ? 'حدد عدد المرات التي يستطيع كل حلاق فيها استخدام رمز الإذن، أو أوقف الخاصية له تماماً. (0 = بدون حد)'
            : 'Set how many times each barber can use the override-code feature, or disable it entirely. (0 = unlimited)'}
        </p>
        <div className="text-xs text-muted-foreground mt-2">
          {isArabic ? 'إجمالي الاستخدامات' : 'Total usages'}: <span className="font-bold text-amber-400">{totalUsed}</span>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-1" />
          {isArabic ? 'جاري التحميل...' : 'Loading...'}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">
          {isArabic ? 'لا يوجد حلاقين' : 'No barbers'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr>
                <th className="text-start py-3 px-3">{isArabic ? 'الحلاق' : 'Barber'}</th>
                <th className="text-center py-3 px-3">{isArabic ? 'الحد الأقصى' : 'Limit'}</th>
                <th className="text-center py-3 px-3">{isArabic ? 'المستخدم' : 'Used'}</th>
                <th className="text-center py-3 px-3">{isArabic ? 'المتبقي' : 'Remaining'}</th>
                <th className="text-center py-3 px-3">{isArabic ? 'الحالة' : 'Status'}</th>
                <th className="text-end py-3 px-3">{isArabic ? 'إجراء' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const edited = edits[it.user_id];
                const displayLimit = edited?.limit !== undefined ? edited.limit : it.limit;
                const isSaving = saving === it.user_id;
                const overflowing = it.limit > 0 && it.used >= it.limit;
                return (
                  <tr key={it.user_id} className="border-t border-white/5" data-testid={`quota-row-${it.user_id}`}>
                    <td className="py-3 px-3 font-medium">{it.user_name || '—'}</td>
                    <td className="py-3 px-3 text-center">
                      <Input
                        type="number"
                        min="0"
                        value={displayLimit}
                        onChange={(e) =>
                          setEdits((curr) => ({ ...curr, [it.user_id]: { limit: e.target.value } }))
                        }
                        className="w-20 text-center bg-secondary/50 mx-auto"
                        data-testid={`limit-input-${it.user_id}`}
                      />
                    </td>
                    <td className="py-3 px-3 text-center font-bold">
                      <span className={overflowing ? 'text-red-400' : 'text-amber-300'}>{it.used}</span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      {it.limit === 0 ? (
                        <span className="text-emerald-400 text-xs font-bold">∞ {isArabic ? 'بدون حد' : 'Unlimited'}</span>
                      ) : (
                        <span className={overflowing ? 'text-red-400 font-bold' : 'text-emerald-400'}>{it.remaining}</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-center">
                      {it.disabled ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 text-xs font-bold border border-red-500/30">
                          <Ban className="w-3 h-3" />
                          {isArabic ? 'موقف' : 'Disabled'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-bold border border-emerald-500/30">
                          <Check className="w-3 h-3" />
                          {isArabic ? 'مفعّل' : 'Active'}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex gap-1 justify-end">
                        {edited !== undefined && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => saveLimit(it.user_id)}
                            disabled={isSaving}
                            className="text-emerald-400 hover:bg-emerald-500/10 border-emerald-500/30"
                            data-testid={`save-quota-${it.user_id}`}
                          >
                            {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleDisabled(it.user_id, it.disabled)}
                          disabled={isSaving}
                          className={
                            it.disabled
                              ? 'text-emerald-400 hover:bg-emerald-500/10 border-emerald-500/30'
                              : 'text-red-400 hover:bg-red-500/10 border-red-500/30'
                          }
                          title={it.disabled ? (isArabic ? 'تفعيل' : 'Enable') : (isArabic ? 'إيقاف' : 'Disable')}
                          data-testid={`toggle-disabled-${it.user_id}`}
                        >
                          {it.disabled ? <Check className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                        </Button>
                        {it.used > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => resetUsed(it.user_id, it.user_name)}
                            disabled={isSaving}
                            className="text-blue-400 hover:bg-blue-500/10 border-blue-500/30"
                            title={isArabic ? 'إعادة العدّاد' : 'Reset counter'}
                            data-testid={`reset-${it.user_id}`}
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default OverrideQuotasTab;
